// The dispatch feeder queue — the actionable queue that FEEDS the board, not a
// report. Three buckets; every unassigned row can be routed in place (pick a tech
// + slot → it lands on the board). Reads GET /scheduling/feeder-queue/ (Appointment-
// anchored, the same source the board reads) and assigns via updateAppointment.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getFeederQueue, listTechnicians } from "../../api/schedulingApi";
import { updateAppointment } from "../../api/appointmentsApi";
import "./FeederQueue.css";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
// datetime-local wants "YYYY-MM-DDTHH:mm" in local time.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function FeederQueue() {
  const [data, setData] = useState(null);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null); // appointment_id being assigned
  const [draft, setDraft] = useState({ tech: "", when: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, t] = await Promise.all([getFeederQueue(), listTechnicians({ page_size: 100 })]);
      setData(q.data);
      const rows = Array.isArray(t.data) ? t.data : t.data?.results || [];
      setTechs(rows);
    } catch (e) {
      toast.error("Could not load the queue");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAssign = (row) => {
    setAssigning(row.appointment_id);
    setDraft({ tech: "", when: toLocalInput(row.start_time) });
  };

  const saveAssign = useCallback(async (row) => {
    if (!draft.tech) { toast.error("Pick a technician"); return; }
    setBusy(true);
    try {
      const start = draft.when ? new Date(draft.when).toISOString() : row.start_time;
      const end = new Date(new Date(start).getTime() + (row.duration_minutes || 60) * 60000).toISOString();
      await updateAppointment(row.appointment_id, {
        assigned_user_id: draft.tech, assigned_to: draft.tech,
        start_time: start, end_time: end, start, end,
      });
      toast.success("Assigned — it's on the board");
      setAssigning(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally {
      setBusy(false);
    }
  }, [draft, load]);

  const techName = useMemo(() => {
    const m = new Map();
    techs.forEach((t) => m.set(String(t.id || t.user_id || t.user), t.name || t.full_name || t.display_name || `Tech ${t.id}`));
    return m;
  }, [techs]);

  if (loading) return <div className="fq"><div className="fq-empty">Loading queue…</div></div>;
  const c = data?.counts || {};

  const Row = ({ row, showAssign = true }) => {
    const open = assigning === row.appointment_id;
    return (
      <div className={`fq-row ${open ? "fq-row-open" : ""}`}>
        <div className="fq-row-main">
          <div className="fq-row-top">
            <span className="fq-cust">{row.customer}</span>
            {row.value ? <span className="fq-val">${Math.round(row.value).toLocaleString()}</span> : null}
          </div>
          <div className="fq-row-sub">
            <span className="fq-svc">{row.service}</span>
            <span className="fq-dot">·</span>
            <span>{fmt(row.start_time)}</span>
            {row.address ? <><span className="fq-dot">·</span><span className="fq-addr">{row.address}</span></> : null}
          </div>
        </div>
        {showAssign && !open ? (
          <button className="fq-assign" onClick={() => openAssign(row)}>Assign</button>
        ) : null}
        {showAssign && open ? (
          <div className="fq-assign-form">
            <select className="fq-select" value={draft.tech} onChange={(e) => setDraft((d) => ({ ...d, tech: e.target.value }))}>
              <option value="">Technician…</option>
              {techs.map((t) => (
                <option key={t.id} value={t.id || t.user_id || t.user}>{t.name || t.full_name || `Tech ${t.id}`}</option>
              ))}
            </select>
            <input className="fq-when" type="datetime-local" value={draft.when} onChange={(e) => setDraft((d) => ({ ...d, when: e.target.value }))} />
            <button className="fq-save" disabled={busy} onClick={() => saveAssign(row)}>{busy ? "…" : "Route it"}</button>
            <button className="fq-cancel" disabled={busy} onClick={() => setAssigning(null)}>Cancel</button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="fq">
      <div className="fq-head">
        <h2>Jobs — dispatch queue</h2>
        <button className="fq-refresh" onClick={load}>Refresh</button>
      </div>

      {/* Unassigned bookings */}
      <section className="fq-bucket">
        <div className="fq-bucket-head">
          <span className="fq-bucket-title">Unassigned bookings</span>
          <span className="fq-bucket-count fq-accent">{c.unassigned || 0}</span>
        </div>
        {data?.unassigned?.length ? (
          data.unassigned.map((r) => <Row key={r.appointment_id} row={r} />)
        ) : (
          <div className="fq-bucket-empty">All booked work is routed. New bookings without a tech land here.</div>
        )}
      </section>

      {/* Due recurring — empty until the materializer */}
      <section className="fq-bucket">
        <div className="fq-bucket-head">
          <span className="fq-bucket-title">Due recurring visits</span>
          <span className="fq-bucket-count">{c.due_recurring || 0}</span>
        </div>
        <div className="fq-bucket-empty">
          Recurring visits will appear here once the materializer generates them from active service plans.
        </div>
      </section>

      {/* Incomplete — started, not finished */}
      <section className="fq-bucket">
        <div className="fq-bucket-head">
          <span className="fq-bucket-title">Incomplete jobs</span>
          <span className="fq-bucket-count fq-warn">{c.incomplete || 0}</span>
        </div>
        {data?.incomplete?.length ? (
          data.incomplete.map((r) => <Row key={r.appointment_id} row={r} showAssign={false} />)
        ) : (
          <div className="fq-bucket-empty">No jobs started and left unfinished.</div>
        )}
      </section>
    </div>
  );
}
